import { useEffect, useRef, useState } from 'react'
import _ from 'lodash'
import { SLIDE_TILE_DEFINITIONS } from '@/config/original'
import { cn } from '@/lib/utils'

interface RoundHistoryBarProps {
	history: number[]
	className?: string
}

export function RoundHistoryBar({ history, className }: RoundHistoryBarProps) {
	const viewportRef = useRef<HTMLDivElement | null>(null)
	const trackRef = useRef<HTMLDivElement | null>(null)
	const dragRef = useRef<{ active: boolean; startX: number; scrollLeft: number }>({ active: false, startX: 0, scrollLeft: 0 })
	const [isDragging, setIsDragging] = useState(false)

	useEffect(() => {
		const viewport = viewportRef.current
		if (!viewport) {
			return
		}

		viewport.scrollLeft = viewport.scrollWidth
	}, [history])

	const handlePointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
		const viewport = viewportRef.current
		if (!viewport || viewport.scrollWidth <= viewport.clientWidth) {
			return
		}

		dragRef.current = {
			active: true,
			startX: event.clientX,
			scrollLeft: viewport.scrollLeft,
		}
		setIsDragging(true)
		viewport.setPointerCapture(event.pointerId)
	}

	const handlePointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
		const viewport = viewportRef.current
		if (!viewport || !dragRef.current.active) {
			return
		}

		const delta = event.clientX - dragRef.current.startX
		viewport.scrollLeft = dragRef.current.scrollLeft - delta
	}

	const handlePointerUp = (event: React.PointerEvent<HTMLDivElement>) => {
		const viewport = viewportRef.current
		if (!viewport || !dragRef.current.active) {
			return
		}

		dragRef.current.active = false
		setIsDragging(false)

		if (viewport.hasPointerCapture(event.pointerId)) {
			viewport.releasePointerCapture(event.pointerId)
		}
	}

	if (history.length === 0) {
		return null
	}

	return (
		<div className={cn('absolute left-1/2 top-3 z-20 w-[min(92%,calc(100%-1rem))] -translate-x-1/2 bg-transparent', className)}>
			<div
				ref={viewportRef}
				className={cn('select-none overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden', isDragging ? 'cursor-grabbing' : 'cursor-grab')}
				onPointerDown={handlePointerDown}
				onPointerMove={handlePointerMove}
				onPointerUp={handlePointerUp}
				onPointerCancel={handlePointerUp}
			>
				<div ref={trackRef} className='flex w-max items-center gap-1.5 px-1 py-0.5'>
					{history.map((slidePoint, index) => {
						const tile = _.findLast(SLIDE_TILE_DEFINITIONS, (item) => slidePoint >= item.point) ?? SLIDE_TILE_DEFINITIONS[0]

						return (
							<span
								key={`${slidePoint}-${index}`}
								className='inline-flex h-6 shrink-0 items-center justify-center rounded-full px-2.5 text-[11px] font-semibold tabular-nums'
								style={{ backgroundColor: tile.color, color: tile.textColor }}
							>
								{Number(slidePoint || 1).toFixed(2)}x
							</span>
						)
					})}
				</div>
			</div>
		</div>
	)
}
